import React, { useState } from "react";
import { API_URLS } from "../../../apiUrl";
import axiosInstance from "../../../components/axiosInstance";

const AddBorough = ({ muniCipalityName, muniCipalityId }: any) => {
  const [boroughNames, setBoroughNames] = useState<string[]>([]); // Array to store borough names
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [formStatus, setFormStatus] = useState<{ type: string; message: string } | null>(null);
  const [inputValue, setInputValue] = useState<any>(null);
  const [isSampleDownloaded, setIsSampleDownloaded] = useState(false);
  const [file, setFile] = useState<File | null>(null); // Store the uploaded file

  // Validate the form
  const validateForm = () => {
    let valid = true;
    const newErrors: { [key: string]: string } = {};

    if (boroughNames.length === 0 && !file) {
      newErrors["borough_name"] = "At least one borough is required or a CSV file needs to be uploaded.";
      valid = false;
    } else {
      // Check if the borough name contains only text and spaces
      const textAndSpacesRegex = /^[A-Za-z0-9\s]+$/; 
      if (!boroughNames.every(borough => textAndSpacesRegex.test(borough))) {
        newErrors["borough_name"] = "Borough name must only contain letters,numbers and spaces.";
        valid = false;
      }
    }
    

    setFormErrors(newErrors);
    return valid;
  };

  // Handle input change (for the text input field)
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  // Add borough name to the array on Enter key press
  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && inputValue.trim() !== "") {
      const newBorough = inputValue.trim();
      if (!boroughNames.includes(newBorough)) {
        setBoroughNames([...boroughNames, newBorough]); // Add borough to the list
      }
      setInputValue(""); // Clear input after adding
      event.preventDefault(); // Prevent form submission when pressing Enter
    }
  };

  // Remove borough name from the array
  const removeBoroughName = (borough: string) => {
    setBoroughNames(boroughNames.filter((name) => name !== borough)); // Remove selected borough
  };


  // Handle form submission for single entry
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    // Join borough names with "#" separator
    const boroughNamesString = boroughNames.join("#");

    try {
      const response = await axiosInstance.post(`${API_URLS.addBorough}`, {
        borough_name: boroughNamesString,  // Send the formatted string
        municipalityTbl_id: muniCipalityId,
      }

    );

      if (response.data.data.addedBoroughs.length > 0) {
        const boroughNames = response.data.data.addedBoroughs.map((borough: any) => borough.borough_name).join(', ');
        setFormStatus({ type: "success", message: `Boroughs inserted successfully: ${boroughNames}` });

      } else if (response.data.data.duplicateBoroughs.length > 0 && response.data.data.addedBoroughs.length == 0) {
        setFormStatus({ type: "exists", message: "Borough already exists for this municipality!" });
      } else {
        setFormStatus({
          type: "error",
          message: "An error occurred while submitting the form. Please try again.",
        });
      }

      setTimeout(() => {
        setFormStatus(null);
      }, 5000);
    } catch (error: any) {
      console.error("Error submitting the form: ", error);
      setFormStatus({
        type: "error",
        message: "An error occurred while submitting the form. Please try again.",
      });
      setTimeout(() => {
        setFormStatus(null);
      }, 5000);
    } finally {
      setBoroughNames([]); // Reset borough names after submission
      setInputValue(""); // Clear input after submission
    }
  };

  // Handle file input (Bulk Upload)
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setFile(file);
    }
  };

  // Handle bulk upload (CSV or Excel)
  const handleBulkUpload = async (event: React.FormEvent) => {
    event.preventDefault();

    // Validate form before submission
    if (!file) {
      setFormStatus({ type: "error", message: "Please upload a file for bulk upload." });
      return;
    }

    // Create FormData to send the file
    const formData = new FormData();
    formData.append("borough_bulk_data", file);

    try {
      const response = await axiosInstance.post(`${API_URLS.BulkBoroughData}`, formData);

      if (response.data.message == "Bulk borough data inserted successfully") {

        setFormStatus({ type: "success", message: `File uploaded successfully` });

      } else {
        setFormStatus({
          type: "error",
          message: "An error occurred during bulk upload. Please try again.",
        });
      }

      setTimeout(() => {
        setFormStatus(null);
      }, 5000);
    } catch (error: any) {
      console.error("Error uploading the file: ", error);
      setFormStatus({
        type: "error",
        message: "An error occurred while uploading the file. Please try again.",
      });
      setTimeout(() => {
        setFormStatus(null);
      }, 5000);
    } finally {
      setFile(null); // Reset the file after submission
    }
  };

  // CSV Section //

  const downloadSampleCSV = () => {
    const sampleData = [
      { municipality: muniCipalityId }
    ];

    const csvContent = [
      ["municipality", "borough"],
      ...sampleData.map((item) => [item.municipality]),
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "borough_data.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setIsSampleDownloaded(true);
  };

  return (
    <div className="max-w-full mx-auto">
      <div className="flex flex-col gap-5">
        <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">Add Borough</h1>
        <div className="p-6 bg-white rounded-lg shadow-lg dark:bg-gray-800">
          <form onSubmit={file ? handleBulkUpload : handleSubmit}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-2">
              <div>
                <label
                  htmlFor="municipalityTbl_id"
                  className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                >
                  Municipality <span className="text-red-600">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={muniCipalityName}
                    id="municipalityTbl_id"
                    name="municipalityTbl_id"
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-0 focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    disabled
                  />
                </div>
              </div>

              <div>
                <label htmlFor="file-upload" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                  Bulk Upload (CSV, Excel)
                </label>
                <div className="flex items-center">
                  <input
                    type="file"
                    id="file-upload"
                    name="file-upload"
                    accept=".csv, .xls, .xlsx"
                    onChange={handleFileChange}
                    disabled={inputValue != null}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-0 focus:ring-blue-500 focus:border-blue-500 block w-full px-2.5 py-1.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="lg:col-span-2 sm:col-span-2">
                <label
                  htmlFor="borough_name"
                  className="block mb-2 text-sm font-medium text-gray-900 dark:text-white leading-3"
                >
                  Borough <span className="text-red-600">*</span>
                </label>
                <div className="text-red-500 block mb-2 text-xs font-medium leading-3">(Type the borough names and press Enter)</div>
                <div className="flex flex-wrap gap-2">
                  {boroughNames.map((borough, index) => (
                    <div className="flex flex-wrap gap-2" key={index}>
                      <div
                        className="bg-blue-200 text-blue-800 px-3 py-1 rounded-full flex items-center gap-1"
                      >
                        {borough}
                        <button
                          type="button"
                          onClick={() => removeBoroughName(borough)}
                          className="text-red-500 text-sm"
                        >
                          &times;
                        </button>
                      </div>
                    </div>
                  ))}

                  <input
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyPress}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-0 focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    placeholder="Type a borough name and press Enter"
                  />
                  {formErrors.borough_name && (
                    <div className="text-red-500 text-sm mt-2">{formErrors.borough_name}</div>
                  )}
                </div>
              </div>

              <div className="lg:col-span-2 sm:col-span-2 mt-3">
                <div className="flex gap-x-2">
                  <button
                    type="button"
                    onClick={downloadSampleCSV}
                    disabled={ inputValue != null || isSampleDownloaded == true} // Disable if single entry is used
                    className="text-white bg-orange-500 hover:bg-orange-600 focus:ring-4 focus:outline-none focus:ring-orange-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-orange-600 dark:hover:bg-orange-600 dark:focus:ring-orange-600 disabled:opacity-50"
                  >
                    Download Sample
                  </button>

                  <button
                    type="submit"
                    disabled={!file} // Disable if no file is selected
                    className="text-white bg-orange-500 hover:bg-orange-600 focus:ring-4 focus:outline-none focus:ring-orange-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-orange-600 dark:hover:bg-orange-600 dark:focus:ring-orange-600 disabled:opacity-50"
                  >
                    Upload CSV
                  </button>

                  <button
                    type="submit"
                    disabled={file != null} 
                    className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800 disabled:opacity-50"
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          </form>

          {/* Display success/error message */}
          {formStatus && (
            <div
              className={`mt-4 p-4 rounded-lg flex justify-between items-center ${formStatus.type === "success"
                ? "bg-green-600 text-white"
                : formStatus.type === "exists"
                  ? "bg-yellow-600 text-white"
                  : "bg-red-600 text-white"
                }`}
            >
              <span>{formStatus.message}</span>
              <button
                className="ml-4 text-white font-bold text-lg focus:outline-none"
                onClick={() => setFormStatus(null)}
              >
                &times;
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddBorough;

